import type { ReactNode } from "react";
import { useCallback, useMemo } from "react";
import { MdChevronLeft, MdChevronRight } from "react-icons/md";
import { Link, useSearchParams } from "react-router-dom";

export default function Pager({ page, linkCount = 9, totalPages, ...props }): ReactNode {
  const [searchParams] = useSearchParams();

  const linkGenerator = useCallback(
    page => {
      searchParams.set("page", page);

      return { search: `?${searchParams.toString()}` };
    },
    [searchParams],
  );

  const midLinkCount = linkCount - 4;

  const elements = useMemo(() => {
    const pages = new Set<number>();
    pages.add(0);

    if (totalPages > 1) {
      pages.add(1);
    }

    if (totalPages > 2) {
      pages.add(totalPages - 2);
      pages.add(totalPages - 1);
    }

    let start = Math.max(3, page - Math.floor(midLinkCount / 2));
    let end = start + midLinkCount;

    if (end > totalPages - 3) {
      end = totalPages - 3;
      start = Math.max(2, end - midLinkCount);
    }

    while (start < end) {
      pages.add(start++);
    }

    let last = 0;
    const elements: ReactNode[] = [];
    if (page === 0) {
      elements.push(
        <a className="prev disabled">
          <MdChevronLeft/>
        </a>,
      );
    } else {
      elements.push(
        <Link to={linkGenerator(page - 1)} className="prev">
          <MdChevronLeft/>
        </Link>,
      );
    }

    for (const i of Array.from(pages.values()).sort((a, b) => a - b)) {
      if (i === last + 2) {
        elements.push(
          <Link to={linkGenerator(i - 1)} className={i - 1 === page ? "active" : undefined}>
            {i}
          </Link>,
        );
      } else if (i > last + 1) {
        elements.push(<div className="spacer">...</div>);
      }

      last = i;
      elements.push(
        <Link to={linkGenerator(i)} className={i === page ? "active" : undefined}>
          {i + 1}
        </Link>,
      );
    }

    if (page === totalPages) {
      elements.push(
        <a className="next disabled">
          <MdChevronRight/>
        </a>,
      );
    } else {
      elements.push(
        <Link to={linkGenerator(page + 1)} className="next">
          <MdChevronRight/>
        </Link>,
      );
    }

    return elements;
  }, [page, totalPages, linkGenerator, midLinkCount]);

  return (
    <div className="pager" {...props}>
      {elements}
    </div>
  );
}
